import { createContext, useContext, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { useAuth, UserProfile } from '@/hooks/useAuth';

export type UserRole = 'driver' | 'fleet_manager' | null;

interface RoleContextValue {
  role: UserRole;
  driverName: string;
  driverUserId: string;
  fleetUser: string;
  authLoading: boolean;
  user: User | null;
  profile: UserProfile | null;
  logout: () => void;
}

const RoleContext = createContext<RoleContextValue | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, profile, loading, signOut } = useAuth();

  // Derive role from profile (Supabase) or user_metadata fallback
  const rawRole = profile?.role ?? (user?.user_metadata?.role as string | undefined);
  const role: UserRole =
    rawRole === 'fleet_manager' || rawRole === 'ceo_admin'
      ? 'fleet_manager'
      : rawRole === 'driver'
      ? 'driver'
      : user
      ? 'driver' // default for authenticated users without profile yet
      : null;

  const driverName =
    profile?.full_name ||
    (user?.user_metadata?.display_name as string) ||
    (user?.user_metadata?.full_name as string) ||
    user?.email?.split('@')[0] ||
    '';

  const fleetUser =
    profile?.full_name ||
    (user?.user_metadata?.display_name as string) ||
    'Fleet Manager';

  const driverUserId = user?.id ?? '';

  return (
    <RoleContext.Provider
      value={{
        role,
        driverName,
        driverUserId,
        fleetUser,
        authLoading: loading,
        user,
        profile,
        logout: signOut,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}
